import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../contexts/UserContext";
import Button from "./common/Button";
import { challenges } from "../data/challengesData";
import "../styles/components/Challenges.css";

// Sample questions data for each challenge
export const challengeQuestions = {
  trig: [
    {
      id: "t1",
      question: "Find the value of sin(30°).",
      options: ["0", "0.5", "1", "√3/2"],
      correctAnswer: "0.5",
      explanation: "sin(30°) = 1/2 = 0.5",
    },
    {
      id: "t2",
      question:
        "In a right triangle, if one angle is 30° and the hypotenuse is 10 cm, what is the length of the opposite side?",
      options: ["5 cm", "8.66 cm", "10 cm", "None of these"],
      correctAnswer: "5 cm",
      explanation:
        "Using the formula: sin(θ) = opposite/hypotenuse. sin(30°) = 0.5, so opposite = 0.5 × 10 = 5 cm",
    },
    {
      id: "t3",
      question: "Find the value of cos(60°).",
      options: ["0", "0.5", "1", "√3/2"],
      correctAnswer: "0.5",
      explanation: "cos(60°) = 1/2 = 0.5",
    },
  ],
  stats: [
    {
      id: "s1",
      question: "What is the mean of the data set: 4, 7, 10, 13, 16?",
      options: ["8", "10", "12", "13"],
      correctAnswer: "10",
      explanation: "Mean = (4 + 7 + 10 + 13 + 16) / 5 = 50 / 5 = 10",
    },
    {
      id: "s2",
      question: "What is the median of the data set: 3, 8, 2, 10, 15?",
      options: ["2", "3", "8", "10"],
      correctAnswer: "8",
      explanation:
        "First arrange in order: 2, 3, 8, 10, 15. The middle value is 8.",
    },
    {
      id: "s3",
      question:
        "The probability of getting a sum of 7 when rolling two fair dice is:",
      options: ["1/6", "1/9", "1/12", "1/36"],
      correctAnswer: "1/6",
      explanation:
        "There are 6 ways to get a sum of 7: (1,6), (2,5), (3,4), (4,3), (5,2), (6,1) out of 36 total possibilities, so 6/36 = 1/6",
    },
  ],
  quadratic: [
    {
      id: "q1",
      question:
        "What are the solutions to the quadratic equation x² - 5x + 6 = 0?",
      options: [
        "x = 2, x = 3",
        "x = -2, x = -3",
        "x = 2, x = -3",
        "x = -2, x = 3",
      ],
      correctAnswer: "x = 2, x = 3",
      explanation:
        "Using the quadratic formula or factoring: x² - 5x + 6 = (x - 2)(x - 3) = 0, so x = 2 or x = 3",
    },
    {
      id: "q2",
      question: "The graph of y = x² + 4x + 4 has its vertex at:",
      options: ["(0, 4)", "(-2, 0)", "(2, 0)", "(4, 0)"],
      correctAnswer: "(-2, 0)",
      explanation: "y = x² + 4x + 4 = (x + 2)² so the vertex is at (-2, 0)",
    },
    {
      id: "q3",
      question:
        "What is the discriminant of the quadratic equation 2x² - 6x + 5 = 0?",
      options: ["36", "16", "4", "-4"],
      correctAnswer: "-4",
      explanation:
        "The discriminant is b² - 4ac = (-6)² - 4(2)(5) = 36 - 40 = -4",
    },
  ],
};

const MathChallenges = () => {
  // const { userData } = useContext(UserContext);
  const [userData, setUserData] = useState({
    name: "Student",
    mathLevel: "Proficient",
    completedChallenges: 7,
    currentStreak: 3,
    totalXP: 320,
    currentRank: "Math Explorer",
    mathSkills: {
      linearEquations: { total: 12, mastered: 8 },
      quadraticEquations: { total: 9, mastered: 3 },
      functionsAndGraphs: { total: 15, mastered: 15 },
    },
    recentTests: [
      {
        id: 1,
        name: "Algebra Mid-Term Test",
        uploadedDays: 2,
        score: 42,
        total: 50,
        grade: "A-",
        strengths: ["Strong in factoring expressions"],
        improvements: ["Practice needed in solving word problems"],
      },
      {
        id: 2,
        name: "Coordinate Geometry Quiz",
        uploadedDays: 7,
        score: 18,
        total: 20,
        grade: "A+",
        strengths: [
          "Excellent at plotting points and lines",
          "Good understanding of distance formula",
        ],
        improvements: [],
      },
    ],
  });
  const navigate = useNavigate();
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  const [score, setScore] = useState(0);

  const startChallenge = (challengeId) => {
    // Find the challenge object
    const challenge = challenges.find((c) => c.id === challengeId);

    if (!challenge) return;

    // Set the selected challenge
    setSelectedChallenge(challenge);

    // Get the appropriate questions based on the challenge type
    let questions = [];
    if (challenge.id === "trig") questions = challengeQuestions.trig;
    else if (challenge.id === "stats") questions = challengeQuestions.stats;
    else if (challenge.id === "quadratic")
      questions = challengeQuestions.quadratic;

    setCurrentQuestions(questions);
    setSelectedAnswer({});
    setShowAnswers(false);
    setScore(0);
  };

  const handleAnswerSelect = (questionId, answer) => {
    setSelectedAnswer({
      ...selectedAnswer,
      [questionId]: answer,
    });
  };

  const submitAnswers = () => {
    let newScore = 0;

    currentQuestions.forEach((question) => {
      if (selectedAnswer[question.id] === question.correctAnswer) {
        newScore++;
      }
    });

    setScore(newScore);
    setShowAnswers(true);
  };

  const resetChallenge = () => {
    setSelectedChallenge(null);
    setCurrentQuestions([]);
    setSelectedAnswer({});
    setShowAnswers(false);
    setScore(0);
  };

  // Render questions view if a challenge is selected
  if (selectedChallenge) {
    return (
      <div className="math-challenge-questions">
        <div className="section-header">
          <div className="icon-container">
            <span className="challenge-icon">{selectedChallenge.icon}</span>
          </div>
          <h2>{selectedChallenge.title}</h2>
        </div>

        <p className="challenge-description">{selectedChallenge.description}</p>

        <div className="questions-container">
          {currentQuestions.map((question, index) => (
            <div key={question.id} className="question-item">
              <h4>Question {index + 1}</h4>
              <p className="question-text">{question.question}</p>

              <div className="options-list">
                {question.options.map((option, optIndex) => (
                  <div
                    key={optIndex}
                    className={`option-item ${selectedAnswer[question.id] === option ? "selected" : ""} 
                    ${showAnswers && option === question.correctAnswer ? "correct" : ""} 
                    ${showAnswers && selectedAnswer[question.id] === option && option !== question.correctAnswer ? "incorrect" : ""}`}
                    onClick={() =>
                      !showAnswers && handleAnswerSelect(question.id, option)
                    }
                  >
                    <span className="option-letter">
                      {String.fromCharCode(65 + optIndex)}
                    </span>
                    <span className="option-text">{option}</span>
                  </div>
                ))}
              </div>

              {showAnswers && (
                <div className="explanation">
                  <p>
                    <strong>Explanation:</strong> {question.explanation}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {showAnswers ? (
          <div className="results-summary">
            <h3>
              Your Score: {score}/{currentQuestions.length}
            </h3>
            <p>
              XP Earned:{" "}
              {(score * selectedChallenge.xpPoints) / currentQuestions.length}{" "}
              XP
            </p>
            <Button variant="primary" fullWidth onClick={resetChallenge}>
              Back to Challenges
            </Button>
          </div>
        ) : (
          <Button variant="primary" fullWidth onClick={submitAnswers}>
            Submit Answers
          </Button>
        )}

        <div className="action-buttons-row">
          <Button variant="secondary" onClick={resetChallenge}>
            Exit Challenge
          </Button>
        </div>
      </div>
    );
  }

  // Render main challenges list view
  return (
    <div className="math-challenges">
      <div className="section-header">
        <div className="icon-container">
          <span className="trophy-icon">🏆</span>
        </div>
        <h2>Aanya's Math Challenges</h2>
      </div>

      <div className="available-challenges">
        <h3>Available Challenges</h3>

        <div className="challenges-list">
          {challenges.map((challenge) => (
            <div
              key={challenge.id}
              className="challenge-item"
              onClick={() => startChallenge(challenge.id)}
            >
              <div className="challenge-icon">{challenge.icon}</div>
              <div className="challenge-details">
                <h4>{challenge.title}</h4>
                <p>{challenge.description}</p>
                <div className="challenge-meta">
                  <span
                    className={`difficulty-badge badge-${challenge.difficulty.toLowerCase()}`}
                  >
                    {challenge.difficulty}
                  </span>
                  <span className="time-badge">
                    <span className="clock-icon">⏱</span>{" "}
                    {challenge.timeRequired} min
                  </span>
                  <span className="xp-badge">
                    <span className="xp-icon">✨</span> {challenge.xpPoints} XP
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="challenge-journey">
        <h3>Your Challenge Journey</h3>

        <div className="journey-stats">
          <div className="stat-row">
            <div className="stat-label">Challenges Completed:</div>
            <div className="stat-value">{userData.completedChallenges}</div>
          </div>
          <div className="stat-row">
            <div className="stat-label">Current Streak:</div>
            <div className="stat-value">{userData.currentStreak} days</div>
          </div>
          <div className="stat-row">
            <div className="stat-label">Total XP Earned:</div>
            <div className="stat-value">{userData.totalXP} XP</div>
          </div>
          <div className="stat-row">
            <div className="stat-label">Current Rank:</div>
            <div className="stat-value">{userData.currentRank}</div>
          </div>
        </div>
      </div>

      <Button
        variant="primary"
        fullWidth
        onClick={() => startChallenge("trig")}
      >
        Start New Challenge
      </Button>
      <div className="action-buttons-row">
        <Button variant="secondary" onClick={() => navigate("/")}>
          Back to Adventure Path
        </Button>
        <Button variant="secondary" onClick={() => navigate("/upload-tests")}>
          Upload Tests
        </Button>
      </div>
    </div>
  );
};

export default MathChallenges;
